import { useRef } from 'react';

import ReactToPrint from 'react-to-print';
import { FcPrint } from 'react-icons/fc';
import styled from 'styled-components';

import { WarehouseDeliveryFragmentFragment } from 'data/__generated__';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { colors } from 'constants/colors';
import { PageHeader } from './PageHeader';

export const WarehouseDeliveryNote = ({
    warehouseDelivery,
}: {
    warehouseDelivery: WarehouseDeliveryFragmentFragment;
}) => {
    const componentRef = useRef(null);
    const { products } = warehouseDelivery;

    return (
        <Container>
            <ReactToPrint
                trigger={() => {
                    // NOTE: could just as easily return <SomeComponent />. Do NOT pass an `onClick` prop
                    // to the root node of the returned component as it will be overwritten.
                    return (
                        <span>
                            <TotemPrimaryButton type="button">
                                <PrintButtonContent>
                                    <FcPrint size={20} />
                                    <PrintLabel>Imprimer</PrintLabel>
                                </PrintButtonContent>
                            </TotemPrimaryButton>
                        </span>
                    );
                }}
                content={() => componentRef.current}
            />
            <HiddenComponent>
                <Printable ref={componentRef}>
                    <PageHeader warehouseDelivery={warehouseDelivery} />
                    <ProductsContainer>
                        <Products>
                            <thead>
                                <tr>
                                    <HeaderCell>Produit</HeaderCell>
                                    <HeaderCell $center>Colis commandés</HeaderCell>
                                    <HeaderCell $center>Colis reçus</HeaderCell>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product) => {
                                    const { productId, location, productLabel, packages, packagesPicked } = product;

                                    return (
                                        <BodyRow key={productId}>
                                            <BodyCell>
                                                <ProductDetails>
                                                    <ProductName>{productLabel}</ProductName>
                                                    {location && (
                                                        <ProductLocation>Emplacement : {location}</ProductLocation>
                                                    )}
                                                </ProductDetails>
                                            </BodyCell>
                                            <BodyCell $center>
                                                <QuantityValue>{packages}</QuantityValue>
                                            </BodyCell>
                                            <BodyCell
                                                $center
                                                $hasError={packagesPicked !== null && packagesPicked !== packages}
                                            >
                                                <QuantityValue>{packagesPicked ?? ''}</QuantityValue>
                                            </BodyCell>
                                        </BodyRow>
                                    );
                                })}
                            </tbody>
                        </Products>
                    </ProductsContainer>
                </Printable>
            </HiddenComponent>
        </Container>
    );
};

const Container = styled.div`
    color: ${colors.black};
`;

const PrintButtonContent = styled.div`
    display: flex;
    align-items: center;
`;

const PrintLabel = styled.span`
    margin-left: 5px;
`;

const HiddenComponent = styled.div`
    display: none;
`;

const Printable = styled.div`
    display: table;

    @page {
        margin: 10mm;
    }
`;

const ProductsContainer = styled.div`
    width: 100%;
    page-break-after: always;
    margin-top: 20px;
`;

const Products = styled.table`
    width: 100%;
    border-spacing: 0;
    margin-top: 10px;
`;

const HeaderCell = styled.th<{ $center?: boolean }>`
    padding: 5px;
    font-size: 22px;
    text-align: ${({ $center }) => ($center ? 'center' : 'left')};
    background-color: ${colors.yellow};
`;

const BodyRow = styled.tr`
    page-break-inside: avoid;
`;

type BodyCellProps = {
    $center?: boolean;
    $hasError?: boolean;
};

const BodyCell = styled.td<BodyCellProps>`
    border-top: 1px solid ${colors.pantinGrey};
    text-align: ${({ $center }) => ($center ? 'center' : 'left')};
    color: ${({ $hasError }) => ($hasError ? colors.pantinGrey : colors.black)};
`;

const ProductDetails = styled.div`
    color: ${colors.black};
`;

const ProductName = styled.div`
    font-size: 20px;
`;

const ProductLocation = styled.div``;

const QuantityValue = styled.span`
    font-size: 22px;
`;
